/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createContext, FC, ReactNode, useContext } from 'react'
import { useLiveAPI, UseLiveAPIResults } from './use-live-api'

const GeminiLiveAPIContext = createContext<UseLiveAPIResults | undefined>(undefined)

export type GeminiLiveAPIProviderProps = {
  children: ReactNode
  url?: string
  apiKey: string
}

export const GeminiLiveAPIProvider: FC<GeminiLiveAPIProviderProps> = ({ url, apiKey, children }) => {
  const liveAPI = useLiveAPI({ url, apiKey })

  return <GeminiLiveAPIContext.Provider value={liveAPI}>{children}</GeminiLiveAPIContext.Provider>
}

export const useGeminiLiveAPIContext = () => {
  const context = useContext(GeminiLiveAPIContext)
  if (!context) {
    throw new Error('useGeminiLiveAPIContext must be used wihin a GeminiLiveAPIProvider')
  }
  return context
}
